import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';
import { media } from '../../styles/brand';

import { CaretRightIcon } from '../../assets/svg/SVGComponents';
import PositionBadge from '../Tournament/PositionBadge';
import {
  ArenaName,
  DetailsList,
  DetailsRow,
  ExpandToggle,
  MedalCell,
  NumberCell,
  PendingTag,
  RankingSection,
  RankingTable,
  RankingTableWrapper,
  RankingTitle,
  SchoolName,
  TeamCell,
  TRankingVariant,
} from './styles';
import { formatClock } from '../../utils/tournament/modalityRules';

export interface IRankingRow {
  id: string;
  /** null = sem posição (desclassificada). */
  position: number | null;
  team_name: string;
  school_name?: string | null;
  /** Arena onde a equipe fez a rodada (telão). */
  arena_name?: string | null;
  category_label?: string;
  points: number;
  /** Pontos de arena (soma das rodadas) — coluna da etapa nacional. */
  arena_points?: number;
  /** Apresentação técnica; null = ainda não avaliada ("pendente"). */
  presentation_points?: number | null;
  /** Tempo da melhor rodada, o critério de desempate. */
  best_round_time?: number | null;
  disqualified?: boolean;
  disqualification_reason?: string | null;
}

interface ITournamentRankingProps {
  title: React.ReactNode;
  /** Ícone antes do título (ex.: TrophyIcon). */
  icon?: React.ReactNode;
  rows: IRankingRow[];
  variant?: TRankingVariant;
  showCategory?: boolean;
  /** Coluna extra no fim (ex.: botões da organização). */
  actionsHeader?: string;
  renderActions?: (row: IRankingRow) => React.ReactNode;
  /** Substitui o "N pts" (ex.: contador animado do telão). */
  renderPoints?: (row: IRankingRow) => React.ReactNode;
  /** Conteúdo ao lado do nome (ex.: selo de subida / NOVO LÍDER). */
  renderBadge?: (row: IRankingRow) => React.ReactNode;
  /** Estilo extra por linha (ex.: destaque de quem pontuou). */
  rowStyle?: (row: IRankingRow) => React.CSSProperties | undefined;
  /** Classe extra por linha (ex.: animação de quem pontuou no telão). */
  rowClassName?: (row: IRankingRow) => string | undefined;
  /** Substitui a medalha/número da posição. */
  renderPosition?: (row: IRankingRow) => React.ReactNode;
  /** Desliza as linhas até a nova posição quando a ordem muda (FLIP). */
  animateLayout?: boolean;
  /** Etapa nacional: colunas Arena e Apresentação antes do total. */
  showBreakdown?: boolean;
  /** Coluna com o tempo da melhor rodada (critério de desempate). */
  showTieTime?: boolean;
}

/** Reordenação com um leve overshoot: a subida "chega" na posição nova. */
const LAYOUT_SPRING = { type: 'spring', bounce: 0.25, duration: 0.9 } as const;

/**
 * Tabela de classificação de um grupo (modalidade × categoria). Compartilhada
 * pela tela de organização e pelo telão, para os dois mostrarem a mesma coisa
 * do mesmo jeito; o telão só troca o visual (`variant="board"`) e liga as
 * animações.
 */
const TournamentRanking: React.FC<ITournamentRankingProps> = ({
  title,
  icon,
  rows,
  variant = 'default',
  showCategory = false,
  actionsHeader,
  renderActions,
  renderPoints,
  renderBadge,
  rowStyle,
  rowClassName,
  renderPosition,
  animateLayout = false,
  showBreakdown = false,
  showTieTime = false,
}) => {
  const Row = animateLayout ? motion.tr : 'tr';

  // No celular a tabela da organização vira posição · equipe · total; o resto
  // (categoria, arena, apresentação, tempo, ações) abre ao tocar na linha.
  const isPhone = useMediaQuery({ query: media.sm });
  const compact = isPhone && variant === 'default';
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const hasDetails =
    showCategory || showBreakdown || showTieTime || !!renderActions;
  const expandable = compact && hasDetails;
  const show = (enabled: boolean) => enabled && !compact;

  const presentationLabel = (row: IRankingRow) =>
    row.presentation_points === null ||
    row.presentation_points === undefined ? (
      <PendingTag $variant={variant}>pendente</PendingTag>
    ) : (
      row.presentation_points
    );

  return (
    <RankingSection $variant={variant}>
      <RankingTitle $variant={variant}>
        {icon}
        {title}
      </RankingTitle>
      <RankingTableWrapper $variant={variant}>
        <RankingTable $variant={variant}>
          <thead>
            <tr>
              <th style={{ width: '3rem' }}>#</th>
              <th style={{ textAlign: 'left' }}>Equipe</th>
              {show(showCategory) && <th>Categoria</th>}
              {show(showBreakdown) && <th>Arena</th>}
              {show(showBreakdown) && <th>Apresentação</th>}
              <th>{showBreakdown || compact ? 'Total' : 'Pontuação Total'}</th>
              {show(showTieTime) && (
                <th title="Critério de desempate: tempo registrado na melhor rodada">
                  Tempo · melhor rodada
                </th>
              )}
              {renderActions && !compact && (
                <th style={{ width: '8rem' }}>{actionsHeader ?? 'Ações'}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const position = row.disqualified ? 999 : (row.position ?? 999);
              const expanded = expandable && expandedId === row.id;
              const toggle = () =>
                setExpandedId(current => (current === row.id ? null : row.id));

              return (
                <React.Fragment key={row.id}>
                  <Row
                    className={rowClassName?.(row)}
                    {...(expandable
                      ? { onClick: toggle, 'aria-expanded': expanded }
                      : {})}
                    {...(animateLayout
                      ? { layout: 'position', transition: LAYOUT_SPRING }
                      : {})}
                    style={{
                      cursor: expandable ? 'pointer' : undefined,
                      opacity: row.disqualified ? 0.6 : 1,
                      background: row.disqualified ? '#fef2f2' : 'transparent',
                      ...rowStyle?.(row),
                    }}
                  >
                    <MedalCell $position={position} $variant={variant}>
                      {renderPosition && !row.disqualified ? (
                        renderPosition(row)
                      ) : (
                        <PositionBadge
                          position={row.position}
                          disqualified={row.disqualified}
                        />
                      )}
                    </MedalCell>
                    <TeamCell>
                      <span
                        style={{
                          textDecoration: row.disqualified
                            ? 'line-through'
                            : 'none',
                        }}
                      >
                        {row.team_name}
                      </span>
                      {renderBadge?.(row)}
                      {expandable && (
                        <ExpandToggle $open={expanded} aria-hidden>
                          <CaretRightIcon
                            stroke="currentColor"
                            strokeWidth={2.5}
                            fill="none"
                          />
                        </ExpandToggle>
                      )}
                      {(row.school_name || row.arena_name) && (
                        <SchoolName $variant={variant}>
                          {row.school_name}
                          {row.school_name && row.arena_name && ' · '}
                          {row.arena_name && (
                            <ArenaName>{row.arena_name}</ArenaName>
                          )}
                        </SchoolName>
                      )}
                    </TeamCell>
                    {show(showCategory) && <td>{row.category_label}</td>}
                    {show(showBreakdown) && (
                      <NumberCell>{row.arena_points ?? 0}</NumberCell>
                    )}
                    {show(showBreakdown) && (
                      <NumberCell>{presentationLabel(row)}</NumberCell>
                    )}
                    <td>
                      {row.disqualified ? (
                        <span
                          style={{
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: '#ef4444',
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em',
                          }}
                          title={row.disqualification_reason || ''}
                        >
                          Desclassificado
                        </span>
                      ) : renderPoints ? (
                        renderPoints(row)
                      ) : (
                        <strong>{row.points} pts</strong>
                      )}
                    </td>
                    {show(showTieTime) && (
                      <NumberCell>
                        {row.disqualified
                          ? '—'
                          : formatClock(row.best_round_time)}
                      </NumberCell>
                    )}
                    {renderActions && !compact && <td>{renderActions(row)}</td>}
                  </Row>
                  {expanded && (
                    <DetailsRow>
                      <td colSpan={3}>
                        <DetailsList>
                          {showCategory && (
                            <div>
                              <dt>Categoria</dt>
                              <dd>{row.category_label}</dd>
                            </div>
                          )}
                          {showBreakdown && (
                            <div>
                              <dt>Arena</dt>
                              <dd>{row.arena_points ?? 0}</dd>
                            </div>
                          )}
                          {showBreakdown && (
                            <div>
                              <dt>Apresentação</dt>
                              <dd>{presentationLabel(row)}</dd>
                            </div>
                          )}
                          {showTieTime && (
                            <div>
                              <dt>Tempo · melhor rodada</dt>
                              <dd>
                                {row.disqualified
                                  ? '—'
                                  : formatClock(row.best_round_time)}
                              </dd>
                            </div>
                          )}
                          {row.disqualified && row.disqualification_reason && (
                            <div>
                              <dt>Motivo</dt>
                              <dd>{row.disqualification_reason}</dd>
                            </div>
                          )}
                        </DetailsList>
                        {renderActions?.(row)}
                      </td>
                    </DetailsRow>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </RankingTable>
      </RankingTableWrapper>
    </RankingSection>
  );
};

export default TournamentRanking;
